import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useSnackbar } from 'notistack';
import { useKYC } from '../context/KYCProvider';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  LinearProgress,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Paper,
  IconButton
} from '@mui/material';
import {
  Person,
  ContactMail,
  Assignment,
  VerifiedUser,
  CloudUpload,
  Delete,
  Visibility,
  CheckCircle,
  Error,
  Warning,
  Info
} from '@mui/icons-material';

const KYCForm = ({ walletAddress, onSubmissionComplete }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { submitKYCApplication, loading } = useKYC();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    middleName: '',
    lastName: '',
    dateOfBirth: '',
    nationality: 'Australian',
    gender: '',
    
    // Contact Information
    email: '',
    phone: '',
    streetAddress: '',
    suburb: '',
    state: '',
    postcode: '',
    country: 'Australia',
    
    // Identity Documents
    primaryId: {
      type: '',
      number: '',
      expiryDate: '',
      issuingAuthority: '',
      file: null
    },
    secondaryId: {
      type: '',
      number: '',
      expiryDate: '',
      issuingAuthority: '',
      file: null
    },
    
    // Additional Information
    occupation: '',
    employer: '',
    sourceOfFunds: '',
    purposeOfAccount: '',
    estimatedActivity: '',
    
    // Declarations
    agreeToTerms: false,
    agreeToPrivacy: false,
    confirmInformation: false,
    
    // Metadata
    walletAddress: walletAddress || '',
    applicationId: '',
    submissionDate: ''
  });

  const australianStates = [
    { code: 'NSW', name: 'New South Wales' },
    { code: 'VIC', name: 'Victoria' },
    { code: 'QLD', name: 'Queensland' },
    { code: 'WA', name: 'Western Australia' },
    { code: 'SA', name: 'South Australia' },
    { code: 'TAS', name: 'Tasmania' },
    { code: 'ACT', name: 'Australian Capital Territory' },
    { code: 'NT', name: 'Northern Territory' }
  ];

  const primaryIdTypes = [
    'Australian Passport',
    'Australian Driver Licence',
    'Australian Proof of Age Card',
    'Foreign Passport with Australian Visa'
  ];

  const secondaryIdTypes = [
    'Medicare Card',
    'Birth Certificate',
    'Citizenship Certificate',
    'Utility Bill (less than 3 months old)',
    'Bank Statement (less than 3 months old)',
    'Council Rates Notice',
    'Electoral Roll Extract'
  ];

  const sourceOfFundsOptions = [
    'Employment/Salary',
    'Business Income',
    'Investment Returns',
    'Inheritance',
    'Gift',
    'Property Sale',
    'Other'
  ];

  const purposeOfAccountOptions = [
    'Carbon Credit Trading',
    'Investment Portfolio',
    'Environmental Projects',
    'Corporate ESG Compliance',
    'Personal Investment',
    'Other'
  ];

  // Generate unique application ID
  React.useEffect(() => {
    if (!formData.applicationId) {
      const timestamp = Date.now();
      const random = Math.random().toString(36).substr(2, 6).toUpperCase();
      setFormData(prev => ({
        ...prev,
        applicationId: `KYC-${timestamp}-${random}`
      }));
    }
  }, [formData.applicationId]);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // File upload handlers with drag and drop
  const createFileDropHandler = useCallback((documentType) => {
    return (acceptedFiles, rejectedFiles) => {
      console.log('Drop handler called:', { acceptedFiles, rejectedFiles, documentType });
      
      if (rejectedFiles && rejectedFiles.length > 0) {
        const rejection = rejectedFiles[0];
        console.log('File rejected:', rejection.file.name, 'Errors:', rejection.errors);
        
        const errorMessages = rejection.errors.map(error => {
          switch (error.code) {
            case 'file-too-large':
              return 'File is too large (max 10MB)';
            case 'file-invalid-type':
              return 'Invalid file type (only JPEG, PNG, PDF allowed)';
            case 'too-many-files':
              return 'Only one file allowed';
            default:
              return error.message;
          }
        });
        
        enqueueSnackbar(`Upload error: ${errorMessages.join(', ')}`, { variant: 'error' });
        return;
      }
      
      const file = acceptedFiles[0];
      if (file) {
        console.log('File accepted:', file.name, 'Type:', file.type, 'Size:', file.size);
        
        // Additional validation
        if (file.size > 10 * 1024 * 1024) {
          enqueueSnackbar('File size must be less than 10MB', { variant: 'error' });
          return;
        }
        
        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
        if (!allowedTypes.includes(file.type)) {
          console.log('File type rejected:', file.type, 'Allowed:', allowedTypes);
          enqueueSnackbar('Only JPEG, PNG, and PDF files are allowed', { variant: 'error' });
          return;
        }

        console.log('File accepted:', file.name, 'Type:', file.type, 'Size:', file.size);

        handleInputChange(`${documentType}.file`, file);
        enqueueSnackbar(`${documentType === 'primaryId' ? 'Primary' : 'Secondary'} ID document uploaded successfully`, 
          { variant: 'success' });
      }
    };
  }, [enqueueSnackbar]);

  const primaryIdDropzone = useDropzone({
    onDrop: createFileDropHandler('primaryId'),
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png'],
      'application/pdf': ['.pdf']
    },
    multiple: false,
    maxSize: 10 * 1024 * 1024, // 10MB
    noClick: false,
    noKeyboard: false,
    useFsAccessApi: false
  });

  const secondaryIdDropzone = useDropzone({
    onDrop: createFileDropHandler('secondaryId'),
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png'],
      'application/pdf': ['.pdf']
    },
    multiple: false,
    maxSize: 10 * 1024 * 1024, // 10MB
    noClick: false,
    noKeyboard: false,
    useFsAccessApi: false
  });

  const removeFile = (documentType) => {
    handleInputChange(`${documentType}.file`, null);
    enqueueSnackbar('File removed', { variant: 'info' });
  };

  const FileUploadArea = ({ dropzone, documentType, label, required = false }) => {
    const file = formData[documentType]?.file;
    const fileInputRef = React.useRef(null);
    
    const handleButtonClick = () => {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    };

    const handleFileInputChange = (event) => {
      const selectedFile = event.target.files[0];
      if (selectedFile) {
        createFileDropHandler(documentType)([selectedFile], []);
      }
    };
    
    return (
      <Card variant="outlined" sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {label} {required && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          
          {!file ? (
            <Box>
              <Box
                {...dropzone.getRootProps()}
                sx={{
                  border: '2px dashed',
                  borderColor: dropzone.isDragActive ? 'primary.main' : 
                             dropzone.isDragReject ? 'error.main' : 'grey.300',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  bgcolor: dropzone.isDragActive ? 'action.hover' : 
                          dropzone.isDragReject ? 'error.light' : 'background.paper',
                  '&:hover': {
                    bgcolor: 'action.hover',
                    borderColor: 'primary.main'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <input {...dropzone.getInputProps()} />
                <CloudUpload sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {dropzone.isDragActive ? 
                    (dropzone.isDragReject ? 'File type not supported' : 'Drop file here') : 
                    'Drag & drop file here'}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  or click to select file
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Supported formats: JPEG, PNG, PDF (max 10MB)
                </Typography>
                {dropzone.isDragReject && (
                  <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                    Please select a valid image (JPEG/PNG) or PDF file
                  </Typography>
                )}
              </Box>
              
              {/* Fallback file input button */}
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,application/pdf"
                  onChange={handleFileInputChange}
                  style={{ display: 'none' }}
                />
                <Button
                  variant="outlined"
                  onClick={handleButtonClick}
                  startIcon={<CloudUpload />}
                  sx={{ mt: 1 }}
                >
                  Choose File
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
              <CheckCircle color="success" />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body1" fontWeight="medium">
                  {file.name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
              </Box>
              <IconButton onClick={() => removeFile(documentType)} color="error" size="small">
                <Delete />
              </IconButton>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const validateStep = (step) => {
    switch (step) {
      case 0: // Personal Information
        return formData.firstName && formData.lastName && formData.dateOfBirth && 
               formData.nationality && formData.gender;
      case 1: // Contact Information
        return formData.email && formData.phone && formData.streetAddress && 
               formData.suburb && formData.state && formData.postcode;
      case 2: // Identity Documents
        return formData.primaryId.type && formData.primaryId.number && 
               formData.primaryId.expiryDate && formData.primaryId.file;
      case 3: // Additional Information
        return formData.occupation && formData.sourceOfFunds && formData.purposeOfAccount;
      case 4: // Declarations
        return formData.agreeToTerms && formData.agreeToPrivacy && formData.confirmInformation;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    } else {
      // Provide specific feedback based on the current step
      let missingFields = [];
      
      switch (activeStep) {
        case 0: // Personal Information
          if (!formData.firstName) missingFields.push('First Name');
          if (!formData.lastName) missingFields.push('Last Name');
          if (!formData.dateOfBirth) missingFields.push('Date of Birth');
          if (!formData.nationality) missingFields.push('Nationality');
          if (!formData.gender) missingFields.push('Gender');
          break;
        case 1: // Contact Information
          if (!formData.email) missingFields.push('Email');
          if (!formData.phone) missingFields.push('Phone');
          if (!formData.streetAddress) missingFields.push('Street Address');
          if (!formData.suburb) missingFields.push('Suburb');
          if (!formData.state) missingFields.push('State');
          if (!formData.postcode) missingFields.push('Postcode');
          break;
        case 2: // Identity Documents
          if (!formData.primaryId.type) missingFields.push('Primary ID Type');
          if (!formData.primaryId.number) missingFields.push('Primary ID Number');
          if (!formData.primaryId.expiryDate) missingFields.push('Primary ID Expiry Date');
          if (!formData.primaryId.file) missingFields.push('Primary ID Document Upload');
          break;
        case 3: // Additional Information
          if (!formData.occupation) missingFields.push('Occupation');
          if (!formData.sourceOfFunds) missingFields.push('Source of Funds');
          if (!formData.purposeOfAccount) missingFields.push('Purpose of Account');
          break;
        case 4: // Declarations
          if (!formData.agreeToTerms) missingFields.push('Terms Agreement');
          if (!formData.agreeToPrivacy) missingFields.push('Privacy Agreement');
          if (!formData.confirmInformation) missingFields.push('Information Confirmation');
          break;
      }
      
      if (missingFields.length > 0) {
        enqueueSnackbar(`Please complete: ${missingFields.join(', ')}`, { variant: 'warning' });
      } else {
        enqueueSnackbar('Please complete all required fields', { variant: 'warning' });
      }
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    try {
      console.log('Starting KYC submission...');
      
      if (!validateStep(4)) {
        enqueueSnackbar('Please complete all required fields and declarations', { variant: 'error' });
        return;
      }

      const submissionData = {
        ...formData,
        submissionDate: new Date().toISOString(),
        walletAddress
      };

      console.log('Submitting KYC data:', {
        ...submissionData,
        primaryId: {
          ...submissionData.primaryId,
          file: submissionData.primaryId.file ? `${submissionData.primaryId.file.name} (${submissionData.primaryId.file.size} bytes)` : null
        },
        secondaryId: {
          ...submissionData.secondaryId,
          file: submissionData.secondaryId.file ? `${submissionData.secondaryId.file.name} (${submissionData.secondaryId.file.size} bytes)` : null
        }
      });

      const result = await submitKYCApplication(submissionData);
      
      console.log('KYC submission successful:', result);
      enqueueSnackbar('KYC application submitted successfully!', { variant: 'success' });
      
      if (onSubmissionComplete) {
        onSubmissionComplete(result);
      }
      
    } catch (error) {
      console.error('KYC submission error:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to submit KYC application. ';
      if (error.message.includes('IPFS')) {
        errorMessage += 'There was an issue uploading your documents. Please check your internet connection and try again.';
      } else if (error.message.includes('network')) {
        errorMessage += 'Network error. Please check your internet connection and try again.';
      } else {
        errorMessage += error.message || 'Please try again.';
      }
      
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const steps = [
    {
      label: 'Personal Information',
      icon: <Person />,
      content: (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Please provide your personal details as they appear on your government-issued ID.
              </Typography>
            </Alert>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="First Name"
              required
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Middle Name"
              value={formData.middleName}
              onChange={(e) => handleInputChange('middleName', e.target.value)}
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Last Name"
              required
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Date of Birth"
              type="date"
              required
              value={formData.dateOfBirth}
              onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth required>
              <InputLabel>Gender</InputLabel>
              <Select
                value={formData.gender}
                onChange={(e) => handleInputChange('gender', e.target.value)}
                label="Gender"
              >
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
                <MenuItem value="Prefer not to say">Prefer not to say</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Nationality"
              required
              value={formData.nationality}
              onChange={(e) => handleInputChange('nationality', e.target.value)}
              placeholder="e.g., Australian"
            />
          </Grid>
        </Grid>
      )
    },
    {
      label: 'Contact Information',
      icon: <ContactMail />,
      content: (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Provide your current residential address and contact details.
              </Typography>
            </Alert>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              required
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone Number"
              required
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              placeholder="+61 4XX XXX XXX"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Street Address"
              required
              value={formData.streetAddress}
              onChange={(e) => handleInputChange('streetAddress', e.target.value)}
              placeholder="Unit/Number Street Name"
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Suburb"
              required
              value={formData.suburb}
              onChange={(e) => handleInputChange('suburb', e.target.value)}
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth required>
              <InputLabel>State</InputLabel>
              <Select
                value={formData.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                label="State"
              >
                {australianStates.map(state => (
                  <MenuItem key={state.code} value={state.code}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Postcode"
              required
              value={formData.postcode}
              onChange={(e) => handleInputChange('postcode', e.target.value)}
              inputProps={{ pattern: '[0-9]{4}', maxLength: 4 }}
            />
          </Grid>
        </Grid>
      )
    },
    {
      label: 'Identity Documents',
      icon: <Assignment />,
      content: (
        <Box>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Important:</strong> Upload clear, high-quality images of your documents. 
              Ensure all text is readable and the document is not expired.
            </Typography>
          </Alert>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Primary ID Document *
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={formData.primaryId.type}
                  onChange={(e) => handleInputChange('primaryId.type', e.target.value)}
                  label="Document Type"
                >
                  {primaryIdTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                label="Document Number"
                required
                value={formData.primaryId.number}
                onChange={(e) => handleInputChange('primaryId.number', e.target.value)}
                sx={{ mb: 2 }}
              />
              
              <TextField
                fullWidth
                label="Expiry Date"
                type="date"
                required
                value={formData.primaryId.expiryDate}
                onChange={(e) => handleInputChange('primaryId.expiryDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
              
              <TextField
                fullWidth
                label="Issuing Authority"
                value={formData.primaryId.issuingAuthority}
                onChange={(e) => handleInputChange('primaryId.issuingAuthority', e.target.value)}
                placeholder="e.g., Department of Foreign Affairs"
              />
              
              <FileUploadArea
                dropzone={primaryIdDropzone}
                documentType="primaryId"
                label="Upload Primary ID"
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Secondary ID Document (Optional)
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Providing a secondary document strengthens your application.
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={formData.secondaryId.type}
                  onChange={(e) => handleInputChange('secondaryId.type', e.target.value)}
                  label="Document Type"
                >
                  {secondaryIdTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                label="Document Number"
                value={formData.secondaryId.number}
                onChange={(e) => handleInputChange('secondaryId.number', e.target.value)}
                sx={{ mb: 2 }}
              />
              
              <TextField
                fullWidth
                label="Expiry Date (if applicable)"
                type="date"
                value={formData.secondaryId.expiryDate}
                onChange={(e) => handleInputChange('secondaryId.expiryDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
              
              <TextField
                fullWidth
                label="Issuing Authority"
                value={formData.secondaryId.issuingAuthority}
                onChange={(e) => handleInputChange('secondaryId.issuingAuthority', e.target.value)}
                placeholder="e.g., Services Australia"
              />
              
              <FileUploadArea
                dropzone={secondaryIdDropzone}
                documentType="secondaryId"
                label="Upload Secondary ID"
              />
            </Grid>
          </Grid>
        </Box>
      )
    },
    {
      label: 'Additional Information',
      icon: <Info />,
      content: (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                This information helps us understand your intended use of our platform.
              </Typography>
            </Alert>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Occupation"
              required
              value={formData.occupation}
              onChange={(e) => handleInputChange('occupation', e.target.value)}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Employer"
              value={formData.employer}
              onChange={(e) => handleInputChange('employer', e.target.value)}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Source of Funds</InputLabel>
              <Select
                value={formData.sourceOfFunds}
                onChange={(e) => handleInputChange('sourceOfFunds', e.target.value)}
                label="Source of Funds"
              >
                {sourceOfFundsOptions.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Purpose of Account</InputLabel>
              <Select
                value={formData.purposeOfAccount}
                onChange={(e) => handleInputChange('purposeOfAccount', e.target.value)}
                label="Purpose of Account"
              >
                {purposeOfAccountOptions.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Estimated Trading Activity"
              multiline
              rows={3}
              value={formData.estimatedActivity}
              onChange={(e) => handleInputChange('estimatedActivity', e.target.value)}
              placeholder="Please describe your expected trading volume and frequency on our platform"
            />
          </Grid>
        </Grid>
      )
    },
    {
      label: 'Review & Submit',
      icon: <VerifiedUser />,
      content: (
        <Box>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Important:</strong> Please review all information carefully before submitting. 
              False or misleading information may result in account suspension.
            </Typography>
          </Alert>
          
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Personal Information</Typography>
                  <Typography variant="body2">
                    <strong>Name:</strong> {formData.firstName} {formData.middleName} {formData.lastName}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Date of Birth:</strong> {formData.dateOfBirth}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Nationality:</strong> {formData.nationality}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Contact Information</Typography>
                  <Typography variant="body2">
                    <strong>Email:</strong> {formData.email}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Phone:</strong> {formData.phone}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Address:</strong> {formData.streetAddress}, {formData.suburb}, {formData.state} {formData.postcode}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Application Details</Typography>
                  <Typography variant="body2">
                    <strong>Application ID:</strong> {formData.applicationId}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Wallet Address:</strong> {walletAddress}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Primary ID:</strong> {formData.primaryId.type} - {formData.primaryId.number}
                  </Typography>
                  {formData.secondaryId.type && (
                    <Typography variant="body2">
                      <strong>Secondary ID:</strong> {formData.secondaryId.type}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          <Paper sx={{ p: 3, bgcolor: 'background.default' }}>
            <Typography variant="h6" gutterBottom>
              Declarations *
            </Typography>
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.agreeToTerms}
                  onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
                />
              }
              label="I agree to the Terms and Conditions of the Carbon Credit Marketplace"
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.agreeToPrivacy}
                  onChange={(e) => handleInputChange('agreeToPrivacy', e.target.checked)}
                />
              }
              label="I agree to the Privacy Policy and consent to the collection and use of my personal information"
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.confirmInformation}
                  onChange={(e) => handleInputChange('confirmInformation', e.target.checked)}
                />
              }
              label="I confirm that all information provided is true and accurate to the best of my knowledge"
            />
          </Paper>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom textAlign="center">
        Know Your Customer (KYC) Verification
      </Typography>
      
      <Typography variant="body1" color="textSecondary" textAlign="center" sx={{ mb: 4 }}>
        Complete your KYC verification to access all platform features
      </Typography>
      
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      
      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((step, index) => (
          <Step key={step.label}>
            <StepLabel
              icon={step.icon}
              optional={
                index === steps.length - 1 ? (
                  <Typography variant="caption">Final step</Typography>
                ) : null
              }
            >
              <Typography variant="h6">{step.label}</Typography>
            </StepLabel>
            <StepContent>
              <Box sx={{ mb: 2 }}>
                {step.content}
              </Box>
              <Box sx={{ mb: 1 }}>
                <div>
                  {index === steps.length - 1 ? (
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={loading || !validateStep(index)}
                      sx={{ mt: 1, mr: 1 }}
                    >
                      Submit Application
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      disabled={!validateStep(index)}
                      sx={{ mt: 1, mr: 1 }}
                    >
                      Continue
                    </Button>
                  )}
                  <Button
                    disabled={index === 0}
                    onClick={handleBack}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    Back
                  </Button>
                </div>
              </Box>
            </StepContent>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
};

export default KYCForm;